/**
 * AI kanal atribusiyası — bütün layihələr üçün ortaq modul (2026-09-12).
 *
 * Shopify-ın "AI channel attribution"-unun bizim versiyası: ziyarətçinin İLK
 * toxunuşunda referrer + UTM oxunur, 90 günlük first-party cookie-yə yazılır,
 * sifariş yaranarkən sifarişə köçürülür. Heç bir üçüncü tərəf skripti yoxdur;
 * GA4-dən asılı deyil (AI çatlarından gələn trafikin xeyli hissəsi referrer-i
 * kəsir — UTM ilə birlikdə istifadə şərtdir).
 *
 * Eyni qaydalar PHP tərəfdə AiChannel.php-də təkrarlanır; ikisini birlikdə dəyiş.
 */
export const COOKIE_NAME = "ai_attr";
export const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
const HOST_RULES = [
    [/(^|\.)chatgpt\.com$|(^|\.)chat\.openai\.com$|(^|\.)openai\.com$/i, "chatgpt"],
    [/(^|\.)perplexity\.ai$/i, "perplexity"],
    [/(^|\.)copilot\.microsoft\.com$|(^|\.)bing\.com$/i, "copilot"],
    [/(^|\.)gemini\.google\.com$|(^|\.)bard\.google\.com$/i, "gemini"],
    [/(^|\.)claude\.ai$|(^|\.)anthropic\.com$/i, "claude"],
    [/(^|\.)meta\.ai$/i, "meta_ai"],
    [/(^|\.)grok\.com$|(^|\.)x\.ai$/i, "grok"],
    [/(^|\.)deepseek\.com$/i, "deepseek"],
];
/** Referrer host + UTM-dən kanalı çıxar. Bing yalnız Copilot-a məxsus yollarla/UTM ilə sayılır. */
export function detectChannel(referrer, params) {
    const get = (k) => (params instanceof URLSearchParams ? params.get(k) : params[k]) ?? null;
    const src = (get("utm_source") ?? get("ref") ?? "").toLowerCase();
    const med = (get("utm_medium") ?? "").toLowerCase();
    // 1) UTM — ən etibarlı (AI-lar referrer-i tez-tez kəsir)
    if (src) {
        if (/chatgpt|openai/.test(src))
            return "chatgpt";
        if (/perplexity/.test(src))
            return "perplexity";
        if (/copilot/.test(src))
            return "copilot";
        if (/gemini|bard/.test(src))
            return "gemini";
        if (/claude|anthropic/.test(src))
            return "claude";
        if (/meta[_-]?ai/.test(src))
            return "meta_ai";
        if (/grok|x\.ai/.test(src))
            return "grok";
        if (/deepseek/.test(src))
            return "deepseek";
        if (/google/.test(src) && /ai|aimode|sge/.test(med + src))
            return "google_ai";
        if (/\bai\b|assistant|llm/.test(src) || /\bai\b|llm|agentic/.test(med))
            return "other_ai";
    }
    // 2) Referrer host
    if (referrer) {
        try {
            const u = new URL(referrer);
            for (const [re, ch] of HOST_RULES) {
                if (re.test(u.hostname)) {
                    // bing.com yalnız /chat və ya copilot yolunda Copilot sayılır
                    if (ch === "copilot" && /(^|\.)bing\.com$/i.test(u.hostname) && !/\/chat|copilot/i.test(u.pathname + u.search))
                        return null;
                    return ch;
                }
            }
            // Google AI Mode: google.* + udm=50 və ya aep/ai göstəricisi
            if (/(^|\.)google\./i.test(u.hostname) && /udm=50|[?&]aep=|ai_mode|aimode/i.test(u.search))
                return "google_ai";
        }
        catch { /* referrer URL deyil */ }
    }
    return null;
}
/** Brauzerdə: ilk toxunuşu oxu, cookie yoxdursa yaz. Səhifə yüklənəndə bir dəfə çağır. */
export function captureAttribution() {
    if (typeof document === "undefined")
        return null;
    const existing = readAttribution();
    if (existing)
        return existing;
    const params = new URLSearchParams(window.location.search);
    const ref = document.referrer || null;
    const sameSite = ref ? (() => { try {
        return new URL(ref).hostname === window.location.hostname;
    }
    catch {
        return false;
    } })() : false;
    if (sameSite)
        return null; // daxili keçid — ilk toxunuş deyil
    const attr = {
        channel: detectChannel(ref, params),
        referrer: ref,
        utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"), utm_term: params.get("utm_term"),
        landing_path: window.location.pathname,
        first_seen_at: new Date().toISOString(),
    };
    // Yalnız maraqlı hallarda yaz (AI kanalı, UTM və ya xarici referrer) — cookie çirklənməsin
    if (attr.channel || attr.utm_source || ref) {
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(attr))}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax; Secure`;
    }
    return attr;
}
export function readAttribution() {
    if (typeof document === "undefined")
        return null;
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    if (!m)
        return null;
    try {
        return JSON.parse(decodeURIComponent(m[1]));
    }
    catch {
        return null;
    }
}
/** Sifariş göndərərkən FormData/JSON-a əlavə etmək üçün düz sahələr. */
export function attributionFields() {
    const a = readAttribution();
    if (!a)
        return {};
    const out = { attribution_json: JSON.stringify(a) };
    if (a.channel)
        out.ai_channel = a.channel;
    return out;
}
